import React, { useEffect, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, ArrowRight, ArrowLeft, AlertCircle, UserPlus, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { SentinelStage } from "@/components/fx/SentinelStage";

const EXEC_ROLE_DETAILS = {
    Execution_All: "Full execution rights across all engines (Code Review · SU24 · OData).",
    Execution_CodeReview: "ABAP AUTHORITY-CHECK inspections and code-review remediations.",
    Execution_SU24Adjust: "Maintain SU24 proposal consistency and obsolescence.",
    Execution_ODataService: "Inspect OData / SEGW services and DPC_EXT authorization.",
};

export default function Signup() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [questions, setQuestions] = useState([]);
    const [roles, setRoles] = useState([]);
    const [form, setForm] = useState({
        email: "", name: "", mobile: "", password: "", confirm: "",
        selectedRoles: new Set(),
        q1: "", a1: "", q2: "", a2: "", q3: "", a3: "",
    });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [q, r] = await Promise.all([
                    api.get("/auth/security-questions"),
                    api.get("/auth/roles"),
                ]);
                setQuestions(q.data.questions);
                setRoles(r.data.execution_roles);
            } catch (e) { console.warn(e); }
        })();
    }, []);

    if (user) return <Navigate to="/" replace />;

    const toggleRole = (r) => {
        const next = new Set(form.selectedRoles);
        if (next.has(r)) next.delete(r); else next.add(r);
        setForm({ ...form, selectedRoles: next });
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (form.password !== form.confirm) return setError("Passwords do not match");
        if (form.password.length < 8) return setError("Password must be at least 8 characters");
        if (form.selectedRoles.size === 0) return setError("Select at least one execution role");
        const qs = [form.q1, form.q2, form.q3];
        if (new Set(qs.filter(Boolean)).size !== 3) return setError("Choose three different security questions");
        if (!form.a1 || !form.a2 || !form.a3) return setError("All three security answers are required");

        setLoading(true);
        try {
            await api.post("/auth/register", {
                email: form.email.trim().toLowerCase(),
                name: form.name.trim(),
                mobile: form.mobile.trim(),
                password: form.password,
                roles: Array.from(form.selectedRoles),
                security_answers: [
                    { question: form.q1, answer: form.a1 },
                    { question: form.q2, answer: form.a2 },
                    { question: form.q3, answer: form.a3 },
                ],
            });
            toast.success("Account created", { description: "You can now sign in." });
            setDone(true);
            setTimeout(() => nav("/login"), 1800);
        } catch (err) {
            const msg = err?.response?.data?.detail;
            const msgStr = typeof msg === "string" ? msg : (Array.isArray(msg) ? msg.map(m => m.msg).join("; ") : "Registration failed");
            setError(msgStr);
            toast.error(msgStr);
        } finally {
            setLoading(false);
        }
    };

    if (done) return (
        <div className="min-h-screen flex items-center justify-center bg-[var(--bg-base)] p-6">
            <div className="panel-elevated p-10 max-w-md text-center">
                <CheckCircle2 size={42} className="text-emerald-400 mx-auto mb-4" />
                <h2 className="font-display text-2xl text-white">Registration complete</h2>
                <p className="text-sm text-slate-400 mt-2">Redirecting you to sign-in…</p>
            </div>
        </div>
    );

    const field = (k, ph, type = "text", extra = {}) => (
        <input
            data-testid={`signup-${k}-input`}
            type={type}
            value={form[k]}
            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            placeholder={ph}
            className="field"
            required
            {...extra}
        />
    );

    return (
        <div className="min-h-screen w-full grid lg:grid-cols-[460px_1fr] relative overflow-hidden" data-testid="signup-page">
            <SentinelStage testId="signup-sentinel-stage" />
            <div className="hidden lg:flex flex-col justify-between p-10 relative isolate border-r border-white/5 z-10">
                <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
                <div className="absolute -left-12 top-1/3 w-72 h-72 rounded-full bg-cyan-500/15 blur-3xl pointer-events-none" />

                <Link to="/login" className="flex items-center gap-3 group relative">
                    <div className="w-11 h-11 rounded-sm bg-gradient-to-br from-cyan-300 to-cyan-600 flex items-center justify-center shadow-[0_0_24px_rgb(var(--accent-1-rgb) / 0.45)]">
                        <Shield size={22} className="text-slate-950" strokeWidth={2.5} />
                    </div>
                    <div>
                        <div className="font-display text-xl text-white">UTSAVA <span className="text-cyan-400">Genesis</span></div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-500/70">um-alphabydesign · Powered by ASSEP</div>
                    </div>
                </Link>

                <div className="relative">
                    <div className="label-eyebrow mb-3">Request Access</div>
                    <h1 className="font-display text-4xl text-white tracking-tight font-light leading-tight">
                        Join the offline<br />
                        <span className="text-cyan-300">SAP&nbsp;Security</span><br />
                        engineering circle.
                    </h1>
                    <p className="mt-5 text-sm text-slate-400 leading-relaxed">
                        Pick the execution roles aligned to your responsibilities.
                        Admin access is reserved for platform owners only.
                    </p>
                </div>

                <Link to="/login" className="btn-ghost w-fit flex items-center gap-2 relative">
                    <ArrowLeft size={12} /> Back to sign-in
                </Link>
            </div>

            <div className="p-6 md:p-10 lg:p-14 overflow-y-auto relative z-10 bg-[#07070A]/72 backdrop-blur-[6px]">
                <motion.form onSubmit={submit} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl space-y-6 relative">
                    <div>
                        <div className="label-eyebrow mb-1">Step 01 — Identity</div>
                        <h2 className="font-display text-2xl text-white">Tell us who you are</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><label className="label-eyebrow block mb-1">Full Name</label>{field("name", "Jane Architect")}</div>
                        <div><label className="label-eyebrow block mb-1">Email Address</label>{field("email", "you@company.com", "email")}</div>
                        <div><label className="label-eyebrow block mb-1">Mobile Number</label>{field("mobile", "+91 9XXXXXXXXX")}</div>
                        <div className="hidden md:block" />
                        <div><label className="label-eyebrow block mb-1">Password</label>{field("password", "min 8 chars", "password")}</div>
                        <div><label className="label-eyebrow block mb-1">Confirm Password</label>{field("confirm", "repeat password", "password")}</div>
                    </div>

                    <div className="pt-2">
                        <div className="label-eyebrow mb-1">Step 02 — Execution Roles</div>
                        <h2 className="font-display text-xl text-white">Which engines will you operate?</h2>
                        <p className="text-xs text-slate-500 mt-1">Select one, multiple, or all. Admin is granted only by platform owners.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {roles.map((r) => {
                            const on = form.selectedRoles.has(r);
                            return (
                                <button
                                    key={r}
                                    type="button"
                                    data-testid={`signup-role-${r}`}
                                    onClick={() => toggleRole(r)}
                                    className={`text-left panel px-4 py-3 transition-colors ${on ? "border-cyan-400/60 bg-cyan-500/10" : "hover:border-cyan-500/30"}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-mono text-[12px] text-cyan-300">{r}</span>
                                        {on && <CheckCircle2 size={14} className="text-cyan-300" />}
                                    </div>
                                    <p className="text-[11px] text-slate-400 mt-1">{EXEC_ROLE_DETAILS[r]}</p>
                                </button>
                            );
                        })}
                    </div>

                    <div className="pt-2">
                        <div className="label-eyebrow mb-1">Step 03 — Account Recovery</div>
                        <h2 className="font-display text-xl text-white">Three security questions</h2>
                        <p className="text-xs text-slate-500 mt-1">Used only to verify identity if you forget your password.</p>
                    </div>
                    {[1, 2, 3].map((n) => {
                        const qKey = `q${n}`;
                        const aKey = `a${n}`;
                        const chosen = new Set([form.q1, form.q2, form.q3].filter(Boolean));
                        return (
                            <div key={n} className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-3">
                                <select
                                    data-testid={`signup-question-${n}`}
                                    value={form[qKey]}
                                    onChange={(e) => setForm({ ...form, [qKey]: e.target.value })}
                                    className="field"
                                    required
                                >
                                    <option value="">{`— Choose question ${n} —`}</option>
                                    {questions.map((q) => (
                                        <option key={q} value={q} disabled={chosen.has(q) && form[qKey] !== q}>{q}</option>
                                    ))}
                                </select>
                                <input
                                    data-testid={`signup-answer-${n}`}
                                    value={form[aKey]}
                                    onChange={(e) => setForm({ ...form, [aKey]: e.target.value })}
                                    placeholder={`Answer ${n}`}
                                    className="field"
                                    required
                                />
                            </div>
                        );
                    })}

                    {error && (
                        <div data-testid="signup-error" className="flex items-start gap-2 p-3 border border-rose-500/30 bg-rose-500/5 text-rose-300 text-xs rounded-sm">
                            <AlertCircle size={14} className="mt-0.5" /><span>{error}</span>
                        </div>
                    )}

                    <div className="flex items-center gap-3 pt-2">
                        <button
                            data-testid="signup-submit"
                            type="submit"
                            disabled={loading}
                            className="btn-primary flex items-center gap-2 disabled:opacity-50"
                        >
                            {loading ? "Creating account…" : "Create account"}
                            {!loading && <ArrowRight size={14} />}
                        </button>
                        <Link to="/login" className="btn-ghost flex items-center gap-2">
                            <ArrowLeft size={12} /> I already have an account
                        </Link>
                    </div>
                </motion.form>
            </div>
        </div>
    );
}
